/* ============================================================================
   NTL — shoppable UGC video modal   (assets/ntl-ugc.js)

   Powers the lightbox behind `sections/ntl-ugc.liquid`. Loaded only by that
   section, deferred, and completely self-guarded: every entry point bails
   quietly when its markup is absent, so a throw here can never take out the
   rest of the page (§5.6).

   Contract with the markup — everything else is a styling detail:
     [data-uvm-open="#id"] [data-uvm-index]   a tile that opens the modal
     dialog[data-uvm]                          the modal root
       [data-uvm-slide="<i>"]                  one clip, `hidden` unless active
         [data-uvm-video]                      <video data-src> (lazy, never preloaded)
         [data-uvm-frame][data-src]            YouTube/Vimeo facade host
         [data-uvm-creator]                    the clip's credit link
         [data-uvm-caption]                    the clip's caption text
       [data-uvm-close] [data-uvm-prev] [data-uvm-next]
       [data-uvm-mute] [data-uvm-bag] [data-uvm-share] [data-uvm-toggle]
       [data-uvm-menu-btn] + [data-uvm-menu]   the ⋮ overflow menu
       [data-uvm-bar]                          progress fill
       [data-uvm-live]                         "Clip 2 of 6" announcer

   The product rails inside each slide are PLAIN `[data-ntl-rail]`s — arrows,
   disabled sync and desktop drag all come from ntl-common.js. Nothing is
   reimplemented here.
   ========================================================================= */
(function () {
  'use strict';

  /* Two UGC sections on one page emit this <script src> twice, and a duplicate
     src DOES execute a second time — which would double-bind the delegated
     click/keydown handlers below. state() is memoised per <dialog>, and
     shopify:section:load rebinds, so one execution covers every instance. */
  if (window.__ntlUgc) return;
  window.__ntlUgc = 1;

  var NTL = window.NTL = window.NTL || {};
  var MOBILE  = window.matchMedia('(max-width:760px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function num(v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; }
  function toast(msg, kind) { if (NTL.toast) NTL.toast(msg, kind); }

  /* ── one state object per <dialog>, memoised on the element ───────────── */
  function state(dlg) {
    if (dlg.__uvm) return dlg.__uvm;

    var st = {
      dlg: dlg,
      sec: dlg.closest('.ntl-ugc') || document,
      slides: $$('[data-uvm-slide]', dlg),
      stage: $('[data-uvm-stage]', dlg),
      bar: $('[data-uvm-bar]', dlg),
      live: $('[data-uvm-live]', dlg),
      menu: $('[data-uvm-menu]', dlg),
      menuBtn: $('[data-uvm-menu-btn]', dlg),
      muteBtn: $('[data-uvm-mute]', dlg),
      bagBtn: $('[data-uvm-bag]', dlg),
      prevBtn: $('[data-uvm-prev]', dlg),
      nextBtn: $('[data-uvm-next]', dlg),
      i: -1,
      open: false,
      muted: dlg.dataset.uvmMuted !== '0',
      wrap: dlg.dataset.uvmWrap === '1',
      advance: dlg.dataset.uvmAdvance === '1',
      anchor: dlg.dataset.uvmAnchor || '',
      opener: null,
      resume: [],
      hash: '',
      scrollY: 0,
      fallback: typeof dlg.showModal !== 'function'
    };
    dlg.__uvm = st;
    wire(st);
    return st;
  }

  /* ── media helpers ───────────────────────────────────────────────────── */
  function slideVideo(sl) { return $('[data-uvm-video]', sl); }

  function mount(sl) {
    /* Give the slide a real source the first time it is shown. Until then the
       <video> carries no src at all, so an unopened modal costs zero bytes. */
    var v = slideVideo(sl);
    if (v && !v.getAttribute('src') && v.dataset.src) {
      v.setAttribute('src', (MOBILE.matches && v.dataset.srcM) || v.dataset.src);
    }
    var host = $('[data-uvm-frame]', sl);
    if (host && !host.dataset.uvmMounted && host.dataset.src) {
      host.dataset.uvmMounted = '1';
      var src = host.dataset.src;
      var f = document.createElement('iframe');
      f.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
      f.setAttribute('allow', 'accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture');
      f.setAttribute('allowfullscreen', '');
      f.setAttribute('title', host.dataset.title || 'Video');
      f.loading = 'lazy';
      host.appendChild(f);
    }
  }

  function unmount(sl) {
    var v = slideVideo(sl);
    if (v) {
      /* Pausing is not enough: a <video> that keeps its src keeps its decoded
         buffer, so six clips watched back to back hold six buffers on a phone.
         Dropping the attribute frees it; coming back re-reads from the HTTP
         cache behind the poster, which is already painted. */
      try { v.pause(); v.removeAttribute('src'); v.load(); } catch (e) {}
    }
    var host = $('[data-uvm-frame]', sl);
    if (host && host.dataset.uvmMounted) {
      /* an iframe keeps playing (and keeps its socket) while hidden — drop it */
      delete host.dataset.uvmMounted;
      var f = $('iframe', host);
      if (f) f.remove();
    }
  }

  function play(st) {
    var v = slideVideo(st.slides[st.i]);
    if (!v) return;
    v.muted = st.muted;
    v.loop = !st.advance;
    var p = v.play();
    if (p && p.catch) p.catch(function () { paint(st); });
  }

  /* ── chrome sync for the active slide ────────────────────────────────── */
  function paint(st) {
    var sl = st.slides[st.i];
    if (!sl) return;
    var v = slideVideo(sl);
    var n = st.slides.length;

    st.dlg.classList.toggle('has-video', !!v);
    st.dlg.classList.toggle('is-paused', !!v && v.paused);

    if (st.prevBtn) st.prevBtn.disabled = !st.wrap && st.i <= 0;
    if (st.nextBtn) st.nextBtn.disabled = !st.wrap && st.i >= n - 1;
    if (n < 2) {
      if (st.prevBtn) st.prevBtn.hidden = true;
      if (st.nextBtn) st.nextBtn.hidden = true;
    }
    if (st.muteBtn) {
      st.muteBtn.hidden = !v;
      st.muteBtn.setAttribute('aria-pressed', st.muted ? 'true' : 'false');
      st.muteBtn.setAttribute('aria-label',
        st.muted ? (st.muteBtn.dataset.on || 'Unmute') : (st.muteBtn.dataset.off || 'Mute'));
    }
    if (st.bagBtn) st.bagBtn.hidden = sl.dataset.uvmShop !== '1';

    /* the ⋮ menu's creator row follows the clip */
    var link = $('[data-uvm-menu-creator]', st.dlg);
    if (link) {
      var href = sl.dataset.uvmCreatorUrl || '';
      var label = sl.dataset.uvmCreator || '';
      link.hidden = !href;
      if (href) {
        link.href = href;
        var t = $('[data-uvm-menu-creator-label]', link);
        if (t && label) t.textContent = label;
      }
    }
    if (st.live) st.live.textContent = (st.live.dataset.tpl || 'Clip [n] of [t]')
      .replace('[n]', st.i + 1).replace('[t]', n);
    if (st.bar) st.bar.style.transform = 'scaleX(0)';
  }

  /* ── move to a slide ─────────────────────────────────────────────────── */
  function go(st, next, dir) {
    var n = st.slides.length;
    if (!n) return;
    if (next < 0)  next = st.wrap ? n - 1 : 0;
    if (next > n - 1) next = st.wrap ? 0 : n - 1;
    if (next === st.i) return;

    var from = st.slides[st.i];
    if (from) { unmount(from); from.hidden = true; from.classList.remove('in', 'up', 'down'); }

    st.i = next;
    var sl = st.slides[st.i];
    sl.hidden = false;
    mount(sl);

    if (!REDUCED.matches) {
      sl.classList.add(dir === -1 ? 'up' : 'down');
      /* force a frame so the enter transition actually runs */
      void sl.offsetHeight;
      sl.classList.remove('up', 'down');
    }
    sl.classList.add('in');

    paint(st);
    play(st);
    syncTray(sl);
    stamp(st);
  }

  function syncTray(sl) {
    var rail = $('[data-ntl-rail]', sl);
    if (rail && NTL.syncRail) { NTL.initRails && NTL.initRails(); NTL.syncRail(rail); }
  }

  /* ── deep link: the address bar always points at the clip on screen ──── */
  function link(st) {
    var base = location.origin + location.pathname + location.search;
    if (!st.anchor) return base;
    return base + '#' + st.anchor + '-' + (st.i + 1);
  }
  function stamp(st) {
    if (!st.anchor || !history.replaceState) return;
    try { history.replaceState(null, '', link(st)); } catch (e) {}
  }

  /* ── scroll lock (mirrors NTL.openPanel: iOS ignores overflow:hidden) ── */
  function lock(st) {
    st.scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -st.scrollY + 'px';
    document.body.style.width = '100%';
  }
  function unlock(st) {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, st.scrollY);
  }

  /* ── open / close ────────────────────────────────────────────────────── */
  function open(dlg, index, opener) {
    var st = state(dlg);
    if (st.open || !st.slides.length) return;
    st.open = true;
    st.opener = opener || null;
    st.hash = location.hash;
    st.muted = dlg.dataset.uvmMuted !== '0';

    /* the rail keeps playing behind a modal dialog — silence it, and put back
       exactly the clips that were running when we close */
    st.resume = [];
    $$('.ugc video', st.sec).forEach(function (v) {
      if (!v.paused) { st.resume.push(v); try { v.pause(); } catch (e) {} }
    });

    lock(st);
    dlg.classList.toggle('is-fallback', st.fallback);
    if (st.fallback) { dlg.setAttribute('open', ''); }
    else { try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); st.fallback = true; } }

    st.i = -1;
    go(st, Math.max(0, Math.min(index || 0, st.slides.length - 1)), 1);
    requestAnimationFrame(function () { dlg.classList.add('on'); });

    var f = $('[data-uvm-close]', dlg);
    if (f) setTimeout(function () { try { f.focus(); } catch (e) {} }, 60);
  }

  function close(dlg) {
    var st = dlg.__uvm;
    if (!st || !st.open) return;
    st.open = false;
    dlg.classList.remove('on');
    shutMenu(st);

    var done = function () {
      st.slides.forEach(function (sl) { unmount(sl); sl.hidden = true; sl.classList.remove('in'); });
      if (st.fallback) dlg.removeAttribute('open');
      else if (dlg.open) { try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); } }
      unlock(st);
      st.resume.forEach(function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); });
      st.resume = [];
      if (st.anchor && history.replaceState) {
        try { history.replaceState(null, '', location.pathname + location.search + st.hash); } catch (e) {}
      }
      if (st.opener && st.opener.focus) { try { st.opener.focus(); } catch (e) {} }
      st.opener = null;
    };
    if (REDUCED.matches) done(); else setTimeout(done, 200);
  }

  /* ── the ⋮ menu ──────────────────────────────────────────────────────── */
  function shutMenu(st) {
    if (!st.menu) return;
    st.menu.hidden = true;
    if (st.menuBtn) st.menuBtn.setAttribute('aria-expanded', 'false');
  }

  /* ── share / copy ────────────────────────────────────────────────────── */
  function copy(text, ok, err) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () { toast(ok); },
        function () { legacy(text, ok, err); });
      return;
    }
    legacy(text, ok, err);
  }
  function legacy(text, ok, err) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
    document.body.appendChild(ta);
    var done = false;
    try { ta.select(); done = document.execCommand('copy'); } catch (e) {}
    ta.remove();
    toast(done ? ok : err, done ? '' : 'err');
  }

  /* ── per-dialog wiring (runs once) ───────────────────────────────────── */
  function wire(st) {
    var dlg = st.dlg;

    dlg.addEventListener('click', function (e) {
      var t = e.target;
      var hit = function (sel) { return t.closest ? t.closest(sel) : null; };

      if (hit('[data-uvm-close]')) { e.preventDefault(); close(dlg); return; }
      if (hit('[data-uvm-prev]')) { go(st, st.i - 1, -1); return; }
      if (hit('[data-uvm-next]')) { go(st, st.i + 1, 1); return; }

      var mute = hit('[data-uvm-mute]');
      if (mute) {
        st.muted = !st.muted;
        var v = slideVideo(st.slides[st.i]);
        if (v) { v.muted = st.muted; if (!st.muted && v.paused) play(st); }
        paint(st);
        return;
      }

      var bag = hit('[data-uvm-bag]');
      if (bag) {
        var off = dlg.classList.toggle('tray-off');
        bag.setAttribute('aria-pressed', off ? 'false' : 'true');
        if (!off) syncTray(st.slides[st.i]);
        return;
      }

      if (hit('[data-uvm-share]')) {
        var url = link(st);
        var title = (st.slides[st.i] && st.slides[st.i].dataset.uvmTitle) || document.title;
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
        } else {
          copy(url, dlg.dataset.uvmCopied || 'Link copied', dlg.dataset.uvmCopyfail || 'Could not copy the link');
        }
        return;
      }

      if (hit('[data-uvm-copy]')) {
        e.preventDefault();
        copy(link(st), dlg.dataset.uvmCopied || 'Link copied', dlg.dataset.uvmCopyfail || 'Could not copy the link');
        shutMenu(st);
        return;
      }

      var mb = hit('[data-uvm-menu-btn]');
      if (mb && st.menu) {
        var show = st.menu.hidden;
        st.menu.hidden = !show;
        mb.setAttribute('aria-expanded', show ? 'true' : 'false');
        return;
      }
      if (!hit('[data-uvm-menu]')) shutMenu(st);

      if (hit('[data-uvm-toggle]')) {
        var vid = slideVideo(st.slides[st.i]);
        if (vid) { if (vid.paused) play(st); else { try { vid.pause(); } catch (er) {} } paint(st); }
        return;
      }

      /* a click on the letterbox around the stage closes, like any lightbox */
      if (!hit('[data-uvm-stage]')) close(dlg);
    });

    /* native ESC (and any other cancel) must run our teardown too */
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(dlg); });
    dlg.addEventListener('close', function () { if (st.open) close(dlg); });

    dlg.addEventListener('keydown', function (e) {
      if (!st.open) return;
      if (e.target.closest && e.target.closest('input,textarea,select')) return;
      var k = e.key;
      if (k === 'ArrowUp' || k === 'ArrowLeft') { e.preventDefault(); go(st, st.i - 1, -1); }
      else if (k === 'ArrowDown' || k === 'ArrowRight') { e.preventDefault(); go(st, st.i + 1, 1); }
      else if (k === 'm' || k === 'M') { var mb2 = st.muteBtn; if (mb2 && !mb2.hidden) mb2.click(); }
      else if (k === ' ' || k === 'k') {
        if (e.target.closest && e.target.closest('button,a')) return;
        e.preventDefault();
        var v2 = slideVideo(st.slides[st.i]);
        if (v2) { if (v2.paused) play(st); else { try { v2.pause(); } catch (er) {} } paint(st); }
      }
    });

    /* progress + autoplay-next, delegated so it covers every slide's <video> */
    st.slides.forEach(function (sl) {
      var v = slideVideo(sl);
      if (!v) return;
      v.addEventListener('timeupdate', function () {
        if (!st.open || st.slides[st.i] !== sl || !st.bar) return;
        var d = v.duration;
        if (!d || !isFinite(d)) return;
        st.bar.style.transform = 'scaleX(' + (v.currentTime / d).toFixed(4) + ')';
      });
      v.addEventListener('ended', function () {
        if (!st.open || st.slides[st.i] !== sl) return;
        if (st.advance && st.slides.length > 1) go(st, st.i + 1, 1);
        else { v.currentTime = 0; play(st); }
      });
      v.addEventListener('play', function () { paint(st); });
      v.addEventListener('pause', function () { paint(st); });
    });

    /* swipe up / down between clips — ignored inside the product tray so the
       horizontal rail keeps its own gestures */
    if (st.stage) {
      var y0 = 0, x0 = 0, live = false;
      st.stage.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1 || (e.target.closest && e.target.closest('[data-ntl-rail]'))) { live = false; return; }
        live = true; y0 = e.touches[0].clientY; x0 = e.touches[0].clientX;
      }, { passive: true });
      st.stage.addEventListener('touchend', function (e) {
        if (!live) return;
        live = false;
        var t = e.changedTouches[0];
        var dy = t.clientY - y0, dx = t.clientX - x0;
        if (Math.abs(dy) < 46 || Math.abs(dy) < Math.abs(dx)) return;
        go(st, st.i + (dy < 0 ? 1 : -1), dy < 0 ? 1 : -1);
      }, { passive: true });
    }

    /* a background tab must not keep a hidden video decoding */
    document.addEventListener('visibilitychange', function () {
      if (!st.open) return;
      var v = slideVideo(st.slides[st.i]);
      if (!v) return;
      if (document.hidden) { try { v.pause(); } catch (e) {} } else play(st);
    });
  }

  /* ── openers ─────────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;                      /* the rail's drag guard */
    var t = e.target.closest ? e.target.closest('[data-uvm-open]') : null;
    if (!t) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
    var dlg = document.querySelector(t.dataset.uvmOpen);
    if (!dlg) return;                                    /* no modal → the link works */
    e.preventDefault();
    open(dlg, num(t.dataset.uvmIndex, 0), t);
  });

  /* keyboard parity for the tile overlay (it is an <a>, so Enter already works;
     Space is added for anyone treating it as a button) */
  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar') return;
    var t = e.target.closest ? e.target.closest('[data-uvm-open]') : null;
    if (!t) return;
    e.preventDefault();
    t.click();
  });

  /* ── deep link on load: /#<anchor>-3 opens clip 3 ────────────────────── */
  function fromHash() {
    var m = /^#([a-zA-Z0-9_-]+)-(\d+)$/.exec(location.hash || '');
    if (!m) return;
    $$('dialog[data-uvm]').forEach(function (dlg) {
      if (dlg.dataset.uvmAnchor !== m[1]) return;
      open(dlg, parseInt(m[2], 10) - 1, null);
    });
  }

  function boot() {
    $$('dialog[data-uvm]').forEach(state);
    fromHash();
  }
  NTL.initUgcModal = boot;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function (e) {
    /* re-open nothing, just (re)bind whatever the editor just re-rendered */
    $$('dialog[data-uvm]', e.target || document).forEach(state);
  });

  /* ── theme editor ────────────────────────────────────────────────────────
     A reel left open covers the section the merchant is trying to edit, so it
     closes with the section; and selecting a clip block while it IS open jumps
     to that clip, which is how you preview one. */
  function openDialogs() { return $$('dialog[data-uvm]').filter(function (d) { return d.__uvm && d.__uvm.open; }); }
  ['shopify:section:select', 'shopify:section:deselect', 'shopify:section:unload'].forEach(function (ev) {
    document.addEventListener(ev, function () { openDialogs().forEach(close); });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var fig = e.target && e.target.closest ? e.target.closest('.uv') : null;
    var link = fig && fig.querySelector('[data-uvm-open]');
    if (!link) return;
    var dlg = document.querySelector(link.dataset.uvmOpen);
    if (dlg && dlg.__uvm && dlg.__uvm.open) go(dlg.__uvm, num(link.dataset.uvmIndex, 0), 1);
  });
})();
